import { DiwaAPI } from './client'

/**
 * Mirrors GET /sigils in products/diwa (the awards/badges surface).
 *
 * A sigil is a suite-wide mark of progression. `earned` is computed server-side from
 * the keeper's own state — auto sigils (streaks, first star, domains) trip from the
 * graph_events log; founder-granted rows (founding-keeper, qa) are stamped onto the ego.
 * Dormant cross-app sigils (Sanctum, Eden) always arrive `earned: false` with a
 * `locked_reason` like "coming with Sanctum" — they light up only when that app ships.
 * The list is owner-scoped: every keeper sees their own earned/locked state.
 */
/**
 * The entitlement DELTA an EARNED sigil grants — composed onto the plan by the backend's
 * entitlements_for. Present on perk-bearing sigils; null (or absent) for cosmetic ones.
 * The gallery shows `label` as what the sigil GRANTS (bright when earned, muted when locked).
 */
export interface SigilPerk {
  /** Human-readable perk shown on the gallery card, e.g. "+10 house missions / mo". */
  label: string
  /** The entitlement axis/flag the delta lands on (house_missions, domains, early_access). */
  kind: string
  /** A number ADDS to a plan axis; a boolean SETS a flag. */
  delta: number | boolean
}

export interface Sigil {
  /** Stable id — the grant key and the how-to-earn anchor. */
  key: string
  name: string
  /** One-sentence what-it-means. */
  gloss: string
  /** The mark itself — a single glyph char (theme-safe, no asset). */
  glyph: string
  /** Coarse rank band — 'foundation' | 'craft' | 'mastery' | 'honorary' (server-defined). */
  tier: string
  /** 'common' | 'uncommon' | 'rare' | 'legendary' | 'mythic'. */
  rarity: string
  /** True for sigils that belong to a sibling app (Sanctum, Eden) — dormant until it ships. */
  cross_app: boolean
  /** Computed from the keeper's state: auto sigils + founder-granted rows on the ego. */
  earned: boolean
  /** ISO timestamp the sigil was earned, or null. */
  earned_at: string | null
  /** Why it isn't earned yet — the how-to-earn line, or "coming with Sanctum" for dormant ones. */
  locked_reason: string | null
  /** What earning this sigil grants (entitlement delta), or null for a purely cosmetic mark. */
  perk?: SigilPerk | null
}

export interface SigilList {
  sigils: Sigil[]
}

export class SigilsAPI {
  private api: DiwaAPI

  constructor(baseUrl: string) {
    this.api = new DiwaAPI(baseUrl)
  }

  /** GET /sigils — every sigil with this keeper's earned/locked state resolved. */
  listSigils(): Promise<SigilList> {
    return this.api.get<SigilList>('/sigils')
  }

  /**
   * POST /sigils/{key}/grant — founder/sovereign-only, idempotent. Stamps a granted
   * row onto the ego (founding-keeper, qa). Guarded server-side to the owner; the UI
   * never exposes it outside the admin surface.
   */
  grant(key: string): Promise<{ granted: boolean }> {
    return this.api.post<{ granted: boolean }>(`/sigils/${encodeURIComponent(key)}/grant`, {})
  }
}
